// src/pages/MenuSettingsPage.js
// 菜單管理（Plan 106 實作、098 訂位預先點菜共用）— JWT + adminOnly（選單層擋 + 後端擋）
//
//   左欄：分類列表（新增/改名/停用/排序 ↑↓/刪除[空分類才可]）＋「全部品項」
//   右欄：品項表（照片縮圖/★/辣度/需預訂/時價/沽清 徽章）＋「編輯」
//   品項編輯＝右側抽屜（106 定案）：照片上傳(S3)/品名/分類/配料說明/份量價格列/
//   加料群組/辣度 0–3/需提前預訂 0–3 天/★招牌/供應中/時價
//   匯入菜單＝dialog：貼文字 → 解析預覽 → 確認匯入（同名分類併入）
//
// 同一份菜單供「外帶點餐」與未來「訂位預先點菜」共用；時價品項不會出現在外帶菜單。
// ============================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, TextField, MenuItem as MuiMenuItem,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Drawer, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, Snackbar, Stack, Skeleton, Checkbox, FormControlLabel, CircularProgress, Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';

import Layout, { loadStoredFontScale, FONT_SCALE_EVENT, useFontScale } from '../components/Layout';
import menuService from '../services/menuService';
import webBookingService from '../services/webBookingService'; // uploadCover＝通用 presigned S3 圖片上傳

const BLUE = '#145da0';

const money = (n) => `NT$${Number(n || 0).toLocaleString()}`;
const priceText = (it) => {
  if (it.kind === 'market_price') return '時價';
  const po = it.priceOptions || [];
  if (!po.length) return '—';
  return po.map((p) => (p.label ? `${p.label} ${money(p.price)}` : money(p.price))).join('／');
};
const chili = (n) => (n > 0 ? '🌶️'.repeat(Math.min(n, 3)) : '');

// 空白品項（新增用）
const emptyDraft = (categoryId) => ({
  id: null, categoryId, name: '', note: '',
  priceOptions: [{ label: '', price: '' }], optionGroups: [],
  kind: 'regular', preorderDays: 1, spicyLevel: 0,
  isSignature: false, isAvailable: true, imageUrl: null,
});

const Badge = ({ bg, fg, children }) => {
  const fontScale = useFontScale(); // 跟隨全域「A」字級，避免放大時標籤過小
  return (
    <Box component="span" sx={{
      display: 'inline-block', px: 0.9, py: '2px', borderRadius: '6px',
      fontSize: Math.round(11 * fontScale), fontWeight: 700, bgcolor: bg, color: fg, ml: 0.75, whiteSpace: 'nowrap', verticalAlign: 'middle',
    }}>
      {children}
    </Box>
  );
};

// headerSx / cellSx 移入元件內（需依全域字級 fontScale 縮放，見元件內定義）

const MenuSettingsPage = () => {
  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });
  const showSnack = (severity, message) => setSnackbar({ open: true, severity, message });

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCat, setSelectedCat] = useState(null);  // null = 全部品項

  // 分類編輯
  const [catDialog, setCatDialog] = useState(null);      // { mode:'create'|'rename', id?, name }
  const [catDeleteTarget, setCatDeleteTarget] = useState(null);

  // 品項編輯抽屜
  const [draft, setDraft] = useState(null);              // null = 關閉
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [itemDeleteTarget, setItemDeleteTarget] = useState(null);

  // 匯入
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);

  // 全域「A」字級：本頁是整頁式 console（m:-3 + calc(100vh)），吃不了 Layout 的 wrapper zoom（會破版），
  // 故 <Layout disableContentZoom> + 自行在固定高容器「內層」套 zoom（見 render）。
  const [fontScale, setFontScale] = useState(() => loadStoredFontScale());
  useEffect(() => {
    const onScale = (e) => setFontScale(typeof e?.detail === 'number' ? e.detail : loadStoredFontScale());
    window.addEventListener(FONT_SCALE_EVENT, onScale);
    return () => window.removeEventListener(FONT_SCALE_EVENT, onScale);
  }, []);
  // 整頁 console 用手動字級（disableContentZoom）→ 各處 fontSize 乘 fontScale，flex 自動重排、不擠壞寬度
  const fz = (base) => Math.round(base * fontScale);
  const headerSx = { bgcolor: '#ddd', fontWeight: 'bold', fontSize: fz(14), position: 'sticky', top: 0, zIndex: 2, borderBottom: '1px solid #ccc' };
  const cellSx = { py: 1.4, fontSize: fz(15) };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const body = await menuService.listCategories();
      if (body?.success) setCategories(body.categories || []);
      else showSnack('error', '載入菜單失敗');
    } catch (e) {
      showSnack('error', '載入菜單失敗');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const allItems = useMemo(
    () => categories.flatMap((c) => (c.items || []).map((it) => ({ ...it, categoryName: c.name, categoryActive: c.isActive }))),
    [categories]
  );
  const shownItems = useMemo(
    () => (selectedCat === null ? allItems : allItems.filter((it) => it.categoryId === selectedCat)),
    [allItems, selectedCat]
  );
  const itemCountOf = (catId) => allItems.filter((it) => it.categoryId === catId).length;

  // ---- 分類操作 ----
  const submitCatDialog = async () => {
    const name = (catDialog?.name || '').trim();
    if (!name) { showSnack('error', '請輸入分類名稱'); return; }
    try {
      const body = catDialog.mode === 'create'
        ? await menuService.createCategory({ name })
        : await menuService.updateCategory(catDialog.id, { name });
      if (body?.success) { setCatDialog(null); load(); }
      else showSnack('error', body?.message || '儲存分類失敗');
    } catch (e) {
      showSnack('error', e?.response?.data?.message || '儲存分類失敗');
    }
  };
  const toggleCatActive = async (c) => {
    try {
      const body = await menuService.updateCategory(c.id, { isActive: !c.isActive });
      if (body?.success) load();
    } catch (e) {
      showSnack('error', '切換失敗');
    }
  };
  const confirmDeleteCat = async () => {
    const c = catDeleteTarget; setCatDeleteTarget(null);
    if (!c) return;
    try {
      const body = await menuService.deleteCategory(c.id);
      if (body?.success) { showSnack('success', '已刪除分類'); if (selectedCat === c.id) setSelectedCat(null); load(); }
    } catch (e) {
      showSnack('error', e?.response?.data?.message || '刪除失敗（分類下還有品項）');
    }
  };
  const moveCat = async (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= categories.length) return;
    const ids = categories.map((c) => c.id);
    [ids[index], ids[next]] = [ids[next], ids[index]];
    try {
      const body = await menuService.reorderCategories(ids);
      if (body?.success) load();
    } catch (e) {
      showSnack('error', '排序失敗');
    }
  };

  // ---- 品項抽屜 ----
  const openCreateItem = () => {
    if (!categories.length) { showSnack('error', '請先新增分類'); return; }
    setDraft(emptyDraft(selectedCat || categories[0].id));
  };
  const openEditItem = (it) => setDraft({
    id: it.id, categoryId: it.categoryId, name: it.name, note: it.note || '',
    priceOptions: (it.priceOptions || []).length ? it.priceOptions.map((p) => ({ ...p })) : [{ label: '', price: '' }],
    optionGroups: (it.optionGroups || []).map((g) => ({ ...g, options: (g.options || []).map((o) => ({ ...o })) })),
    kind: it.kind || 'regular', preorderDays: it.preorderDays ?? 1, spicyLevel: it.spicyLevel ?? 0,
    isSignature: !!it.isSignature, isAvailable: it.isAvailable !== false, imageUrl: it.imageUrl || null,
  });
  const setD = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const onPickPhoto = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const url = await webBookingService.uploadCover(file); // 通用 presigned S3 上傳 → CloudFront URL
      setD('imageUrl', url);
    } catch (err) {
      showSnack('error', '上傳失敗，請稍後再試');
    } finally {
      setUploading(false);
    }
  };

  const saveDraft = async () => {
    if (!draft.name.trim()) { showSnack('error', '請輸入品名'); return; }
    const priceOptions = draft.priceOptions
      .map((p) => ({ label: String(p.label || '').trim(), price: parseInt(p.price, 10) }))
      .filter((p) => Number.isInteger(p.price) && p.price >= 0);
    if (draft.kind !== 'market_price' && !priceOptions.length) { showSnack('error', '請至少設定一個價格'); return; }
    const optionGroups = draft.optionGroups
      .map((g) => ({
        name: String(g.name || '').trim(),
        type: g.type === 'single' ? 'single' : 'multi',
        required: g.required === true,
        options: (g.options || [])
          .map((o) => ({ label: String(o.label || '').trim(), price: parseInt(o.price, 10) || 0 }))
          .filter((o) => o.label),
      }))
      .filter((g) => g.name && g.options.length);

    const payload = {
      categoryId: draft.categoryId,
      name: draft.name.trim(),
      note: draft.note.trim(),
      priceOptions, optionGroups,
      kind: draft.kind, preorderDays: draft.preorderDays, spicyLevel: draft.spicyLevel,
      isSignature: draft.isSignature, isAvailable: draft.isAvailable, imageUrl: draft.imageUrl,
    };
    setSaving(true);
    try {
      const body = draft.id
        ? await menuService.updateItem(draft.id, payload)
        : await menuService.createItem(payload);
      if (body?.success) { showSnack('success', `已儲存「${payload.name}」`); setDraft(null); load(); }
      else showSnack('error', body?.message || '儲存失敗');
    } catch (e) {
      showSnack('error', e?.response?.data?.message || '儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteItem = async () => {
    const it = itemDeleteTarget; setItemDeleteTarget(null);
    if (!it) return;
    try {
      const body = await menuService.deleteItem(it.id);
      if (body?.success) { showSnack('success', `已刪除「${it.name}」`); setDraft(null); load(); }
    } catch (e) {
      showSnack('error', '刪除失敗');
    }
  };

  // ---- 匯入 ----
  const runParse = async () => {
    setImporting(true);
    try {
      const body = await menuService.importMenu(importText, false);
      if (body?.success) setImportPreview(body);
      else showSnack('error', body?.message || '解析失敗');
    } catch (e) {
      showSnack('error', e?.response?.data?.message || '解析失敗');
    } finally {
      setImporting(false);
    }
  };
  const runCommit = async () => {
    setImporting(true);
    try {
      const body = await menuService.importMenu(importText, true);
      if (body?.success) {
        showSnack('success', `已匯入 ${body.created} 道菜，可逐項微調辣度／需預訂／加料群組`);
        setImportOpen(false); setImportText(''); setImportPreview(null);
        load();
      } else showSnack('error', body?.message || '匯入失敗');
    } catch (e) {
      showSnack('error', e?.response?.data?.message || '匯入失敗');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Layout disableContentZoom>
      <Box sx={{ m: -3, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ flexShrink: 0, bgcolor: 'white', borderBottom: '1px solid #e0e0e0', px: 3, py: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography sx={{ fontSize: fz(22), fontWeight: 700 }}>菜單管理</Typography>
              <Typography sx={{ fontSize: fz(13), color: '#999' }}>
                同一份菜單供「外帶點餐」與未來「訂位預先點菜」共用；「時價」品項不會出現在外帶菜單
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" size="small" onClick={() => { setImportOpen(true); setImportPreview(null); }}
                sx={{ borderColor: BLUE, color: BLUE, textTransform: 'none', fontWeight: 700 }}>
                📋 匯入菜單（貼文字）
              </Button>
              <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreateItem}
                sx={{ bgcolor: BLUE, '&:hover': { bgcolor: '#0e447a' }, textTransform: 'none', fontWeight: 700 }}>
                新增品項
              </Button>
            </Stack>
          </Box>
        </Box>

        {/* Body：左分類欄 + 右品項表 */}
        <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* 分類欄 */}
          <Box sx={{ width: fz(210), flexShrink: 0, bgcolor: 'white', borderRight: '1px solid #e0e0e0', overflowY: 'auto', py: 1 }}>
            <Box onClick={() => setSelectedCat(null)}
              sx={{
                mx: 1, px: 1.5, py: 1.1, borderRadius: 1.5, cursor: 'pointer', fontSize: fz(14),
                fontWeight: selectedCat === null ? 700 : 500,
                bgcolor: selectedCat === null ? '#e8f0f8' : 'transparent',
                color: selectedCat === null ? BLUE : '#546e7a',
                '&:hover': { bgcolor: selectedCat === null ? '#e8f0f8' : '#f5f7fa' },
              }}>
              全部品項 <Box component="span" sx={{ fontSize: fz(12), color: '#999' }}>{allItems.length}</Box>
            </Box>
            {categories.map((c, i) => {
              const on = selectedCat === c.id;
              return (
                <Box key={c.id} onClick={() => setSelectedCat(c.id)}
                  sx={{
                    mx: 1, px: 1.5, py: 0.9, borderRadius: 1.5, cursor: 'pointer', fontSize: fz(14),
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    fontWeight: on ? 700 : 500,
                    bgcolor: on ? '#e8f0f8' : 'transparent',
                    color: c.isActive ? (on ? BLUE : '#546e7a') : '#b0bac5',
                    '&:hover': { bgcolor: on ? '#e8f0f8' : '#f5f7fa', '& .cat-actions': { opacity: 1 } },
                  }}>
                  <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.name}{!c.isActive && '（停用）'}
                    <Box component="span" sx={{ fontSize: fz(12), color: '#999', ml: 0.5 }}>{itemCountOf(c.id)}</Box>
                  </Box>
                  <Stack direction="row" className="cat-actions" sx={{ opacity: 0, transition: 'opacity .15s', flexShrink: 0 }}>
                    <IconButton size="small" disabled={i === 0} onClick={(e) => { e.stopPropagation(); moveCat(i, -1); }} sx={{ p: 0.25 }}>
                      <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton size="small" disabled={i === categories.length - 1} onClick={(e) => { e.stopPropagation(); moveCat(i, 1); }} sx={{ p: 0.25 }}>
                      <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setCatDialog({ mode: 'rename', id: c.id, name: c.name }); }} sx={{ p: 0.25 }}>
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); setCatDeleteTarget(c); }} sx={{ p: 0.25 }}>
                      <DeleteOutlineIcon sx={{ fontSize: 14, color: '#d32f2f' }} />
                    </IconButton>
                  </Stack>
                </Box>
              );
            })}
            <Button fullWidth size="small" startIcon={<AddIcon />} onClick={() => setCatDialog({ mode: 'create', name: '' })}
              sx={{ mt: 1, color: '#999', textTransform: 'none', fontSize: fz(13) }}>
              新增分類
            </Button>
          </Box>

          {/* 品項表 */}
          <Box sx={{
            flex: 1, overflow: 'auto', px: 3, minWidth: 0,
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '3px' },
          }}>
            <TableContainer component={Paper} sx={{ mt: 2, mb: 2, boxShadow: '0 2px 4px rgba(0,0,0,0.06)' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerSx}>品項</TableCell>
                    <TableCell sx={headerSx}>份量／價格</TableCell>
                    <TableCell sx={headerSx}>加料群組</TableCell>
                    <TableCell sx={headerSx}>分類</TableCell>
                    <TableCell sx={headerSx}>狀態</TableCell>
                    <TableCell sx={headerSx} align="center">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && (
                    Array.from({ length: 8 }).map((_, i) => (
                      <TableRow key={`sk-${i}`}>
                        {Array.from({ length: 6 }).map((__, j) => (
                          <TableCell key={j} sx={cellSx}><Skeleton variant="text" /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                  {!loading && shownItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#999' }}>
                        <MenuBookIcon sx={{ fontSize: 48, color: '#ddd', mb: 1, display: 'block', mx: 'auto' }} />
                        <Typography>尚無品項 — 按右上「匯入菜單」貼整份文字一次建好，或「新增品項」逐筆建</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {shownItems.map((it) => (
                    <TableRow key={it.id} onClick={() => openEditItem(it)}
                      sx={{ cursor: 'pointer', opacity: it.isAvailable ? 1 : 0.55, '&:hover': { bgcolor: '#f0f4ff !important' } }}>
                      <TableCell sx={cellSx}>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          {it.imageUrl ? (
                            <Box component="img" src={it.imageUrl} alt="" sx={{ width: 44, height: 36, borderRadius: 1, objectFit: 'cover', flexShrink: 0 }} />
                          ) : (
                            <Box sx={{ width: 44, height: 36, borderRadius: 1, bgcolor: '#f1f5f9', color: '#cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 18 }}>🍲</Box>
                          )}
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: fz(15), fontWeight: 700, lineHeight: 1.3 }}>
                              {it.isSignature && <Box component="span" sx={{ color: '#f59e0b' }}>★ </Box>}
                              {it.name} {chili(it.spicyLevel)}
                              {it.kind === 'market_price' && <Badge bg="#ffedd5" fg="#c2410c">時價・不上外帶</Badge>}
                              {it.kind === 'preorder' && <Badge bg="#f3e8ff" fg="#7e22ce">需預訂 {it.preorderDays ?? 1} 天</Badge>}
                            </Typography>
                            {it.note && <Typography sx={{ fontSize: fz(12), color: '#999' }} noWrap>{it.note}</Typography>}
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>{priceText(it)}</TableCell>
                      <TableCell sx={cellSx}>
                        {(it.optionGroups || []).length
                          ? it.optionGroups.map((g) => (
                            <Badge key={g.name} bg="#f1f5f9" fg="#475569">
                              {g.name}：{(g.options || []).map((o) => `${o.label} +${o.price}`).join('／')}
                            </Badge>
                          ))
                          : <Typography component="span" sx={{ color: '#ccc' }}>—</Typography>}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, color: '#546e7a', whiteSpace: 'nowrap' }}>{it.categoryName}</TableCell>
                      <TableCell sx={cellSx}>
                        {it.isAvailable
                          ? <Badge bg="#dcfce7" fg="#15803d">供應中</Badge>
                          : <Badge bg="#e2e8f0" fg="#64748b">沽清</Badge>}
                      </TableCell>
                      <TableCell sx={cellSx} align="center">
                        <Button size="small" onClick={(e) => { e.stopPropagation(); openEditItem(it); }}
                          sx={{ color: BLUE, fontWeight: 700, minWidth: 0 }}>
                          編輯
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Box>

      {/* ═══ 品項編輯抽屜（右側 460px）═══ */}
      <Drawer anchor="right" open={!!draft} onClose={() => setDraft(null)}
        PaperProps={{ sx: { width: 460, maxWidth: '94vw' } }}>
        {draft && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{
              px: 2.5, py: 1.75, borderBottom: '1px solid #e0e0e0', bgcolor: '#f6f9fc',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
            }}>
              <Typography sx={{ fontSize: 18, fontWeight: 800 }}>{draft.id ? '編輯品項' : '新增品項'}</Typography>
              <IconButton onClick={() => setDraft(null)}><CloseIcon /></IconButton>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
              {/* 照片 */}
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                {draft.imageUrl ? (
                  <Box component="img" src={draft.imageUrl} alt="" sx={{ width: 96, height: 78, borderRadius: 1.5, objectFit: 'cover' }} />
                ) : (
                  <Box sx={{ width: 96, height: 78, borderRadius: 1.5, bgcolor: '#f1f5f9', color: '#cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>🍲</Box>
                )}
                <Box>
                  <Button component="label" size="small" variant="outlined" startIcon={uploading ? <CircularProgress size={14} /> : <PhotoCameraIcon />}
                    disabled={uploading} sx={{ textTransform: 'none', borderColor: '#cbd5e1', color: '#475569' }}>
                    {uploading ? '上傳中…' : '上傳照片'}
                    <input hidden type="file" accept="image/*" onChange={onPickPhoto} />
                  </Button>
                  {draft.imageUrl && (
                    <Button size="small" onClick={() => setD('imageUrl', null)} sx={{ ml: 1, color: '#999', textTransform: 'none' }}>移除</Button>
                  )}
                  <Typography sx={{ fontSize: 11, color: '#999', mt: 0.5 }}>選填・建議橫式；上傳後存 S3</Typography>
                </Box>
              </Stack>

              <TextField label="品名 *" size="small" fullWidth value={draft.name} onChange={(e) => setD('name', e.target.value)} sx={{ mb: 2 }} />
              <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                <TextField label="分類" size="small" select fullWidth value={draft.categoryId} onChange={(e) => setD('categoryId', parseInt(e.target.value, 10))}>
                  {categories.map((c) => <MuiMenuItem key={c.id} value={c.id}>{c.name}</MuiMenuItem>)}
                </TextField>
                <TextField label="配料說明（選填）" size="small" fullWidth value={draft.note} placeholder="例：魷魚、豆干、五花肉"
                  onChange={(e) => setD('note', e.target.value)} />
              </Stack>

              {/* 份量／價格 */}
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#546e7a', mb: 0.75 }}>份量／價格</Typography>
              {draft.priceOptions.map((p, i) => (
                <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <TextField size="small" placeholder="份量（單一價留空）" value={p.label} sx={{ width: 150 }}
                    onChange={(e) => setD('priceOptions', draft.priceOptions.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
                  <TextField size="small" placeholder="價格" type="number" value={p.price} fullWidth
                    InputProps={{ startAdornment: <Typography sx={{ fontSize: 13, color: '#999', mr: 0.5 }}>NT$</Typography> }}
                    onChange={(e) => setD('priceOptions', draft.priceOptions.map((x, j) => (j === i ? { ...x, price: e.target.value } : x)))} />
                  <IconButton size="small" onClick={() => setD('priceOptions', draft.priceOptions.filter((_, j) => j !== i))}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Stack>
              ))}
              <Button size="small" startIcon={<AddIcon />} onClick={() => setD('priceOptions', [...draft.priceOptions, { label: '', price: '' }])}
                sx={{ color: BLUE, textTransform: 'none', fontSize: 12.5, mb: 2 }}>
                新增份量（小/中/大…）
              </Button>

              {/* 加料群組 */}
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#546e7a', mb: 0.75 }}>加料群組（客人在品項詳情頁看到）</Typography>
              {draft.optionGroups.map((g, gi) => (
                <Paper key={gi} variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: '#fafbfc', borderRadius: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <TextField size="small" placeholder="群組名稱（例：加購）" value={g.name} fullWidth
                      onChange={(e) => setD('optionGroups', draft.optionGroups.map((x, j) => (j === gi ? { ...x, name: e.target.value } : x)))} />
                    <Button size="small" onClick={() => setD('optionGroups', draft.optionGroups.filter((_, j) => j !== gi))}
                      sx={{ color: '#d32f2f', fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0 }}>
                      刪除群組
                    </Button>
                  </Stack>
                  {(g.options || []).map((o, oi) => (
                    <Stack key={oi} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <TextField size="small" placeholder="選項（例：加麵條）" value={o.label} fullWidth
                        onChange={(e) => setD('optionGroups', draft.optionGroups.map((x, j) => (j === gi
                          ? { ...x, options: x.options.map((y, k) => (k === oi ? { ...y, label: e.target.value } : y)) } : x)))} />
                      <TextField size="small" placeholder="加價" type="number" value={o.price} sx={{ width: 130, flexShrink: 0 }}
                        InputProps={{ startAdornment: <Typography sx={{ fontSize: 13, color: '#999', mr: 0.5 }}>＋NT$</Typography> }}
                        onChange={(e) => setD('optionGroups', draft.optionGroups.map((x, j) => (j === gi
                          ? { ...x, options: x.options.map((y, k) => (k === oi ? { ...y, price: e.target.value } : y)) } : x)))} />
                      <IconButton size="small" onClick={() => setD('optionGroups', draft.optionGroups.map((x, j) => (j === gi
                        ? { ...x, options: x.options.filter((_, k) => k !== oi) } : x)))}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Stack>
                  ))}
                  <Button size="small" startIcon={<AddIcon />} sx={{ color: BLUE, textTransform: 'none', fontSize: 12.5 }}
                    onClick={() => setD('optionGroups', draft.optionGroups.map((x, j) => (j === gi
                      ? { ...x, options: [...(x.options || []), { label: '', price: '' }] } : x)))}>
                    新增選項
                  </Button>
                </Paper>
              ))}
              <Button size="small" startIcon={<AddIcon />} sx={{ color: BLUE, textTransform: 'none', fontSize: 12.5, mb: 2 }}
                onClick={() => setD('optionGroups', [...draft.optionGroups, { name: '加購', type: 'multi', required: false, options: [{ label: '', price: '' }] }])}>
                新增加料群組
              </Button>
              <Typography sx={{ fontSize: 11, color: '#999', mt: -1.5, mb: 2 }}>例：「加購」→ 加麵條 +40。未來套餐／組合也掛在這裡。</Typography>

              {/* 辣度 */}
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#546e7a', mb: 0.75 }}>辣度</Typography>
              <Stack direction="row" spacing={0.75} sx={{ mb: 2 }}>
                {[0, 1, 2, 3].map((n) => {
                  const on = draft.spicyLevel === n;
                  return (
                    <Box key={n} onClick={() => setD('spicyLevel', n)}
                      sx={{
                        px: 1.75, py: 0.6, borderRadius: 1.5, cursor: 'pointer', fontSize: 13, fontWeight: 700,
                        border: '1px solid', borderColor: on ? BLUE : '#d5dbe3',
                        bgcolor: on ? BLUE : 'white', color: on ? 'white' : '#546e7a',
                      }}>
                      {n === 0 ? '不辣' : '🌶️'.repeat(n)}
                    </Box>
                  );
                })}
              </Stack>

              {/* 需提前預訂 */}
              <TextField label="需提前預訂" size="small" select fullWidth sx={{ mb: 0.5 }}
                value={draft.kind === 'preorder' ? draft.preorderDays : 0}
                onChange={(e) => {
                  const d = parseInt(e.target.value, 10);
                  setDraft((cur) => ({ ...cur, preorderDays: d || 1, kind: d > 0 ? 'preorder' : (cur.kind === 'preorder' ? 'regular' : cur.kind) }));
                }}>
                <MuiMenuItem value={0}>不需預訂（當天可取）</MuiMenuItem>
                {[1, 2, 3].map((d) => <MuiMenuItem key={d} value={d}>需提前 {d} 天</MuiMenuItem>)}
              </TextField>
              <Typography sx={{ fontSize: 11, color: '#999', mb: 2 }}>系統會自動限制客人可選的取餐日期，不需電話確認。</Typography>

              {/* 開關列 */}
              <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                <FormControlLabel control={<Checkbox checked={draft.isSignature} onChange={(e) => setD('isSignature', e.target.checked)} sx={{ '&.Mui-checked': { color: '#f59e0b' } }} />}
                  label={<Typography sx={{ fontSize: 14 }}>★ 招牌推薦</Typography>} />
                <FormControlLabel control={<Checkbox checked={draft.isAvailable} onChange={(e) => setD('isAvailable', e.target.checked)} sx={{ '&.Mui-checked': { color: '#2e7d32' } }} />}
                  label={<Typography sx={{ fontSize: 14 }}>供應中</Typography>} />
                <FormControlLabel control={<Checkbox checked={draft.kind === 'market_price'} onChange={(e) => setD('kind', e.target.checked ? 'market_price' : 'regular')} sx={{ '&.Mui-checked': { color: '#c2410c' } }} />}
                  label={<Typography sx={{ fontSize: 14 }}>時價</Typography>} />
              </Stack>
              {draft.kind === 'market_price' && (
                <Alert severity="warning" sx={{ mt: 1, borderRadius: 2, fontSize: 13 }}>
                  時價品項<b>不會出現在外帶菜單</b>（僅內用／預先點菜顯示「時價」字樣）
                </Alert>
              )}

              {draft.id && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Button size="small" startIcon={<DeleteOutlineIcon />} onClick={() => setItemDeleteTarget(draft)}
                    sx={{ color: '#d32f2f', textTransform: 'none' }}>
                    刪除此品項
                  </Button>
                </>
              )}
            </Box>

            <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid #e0e0e0', flexShrink: 0 }}>
              <Button fullWidth variant="contained" disabled={saving} onClick={saveDraft}
                sx={{ bgcolor: BLUE, '&:hover': { bgcolor: '#0e447a' }, py: 1.25, fontSize: 16, fontWeight: 700, textTransform: 'none', borderRadius: 2 }}>
                {saving ? '儲存中…' : '儲存品項'}
              </Button>
            </Box>
          </Box>
        )}
      </Drawer>

      {/* 分類 新增/改名 dialog */}
      <Dialog open={!!catDialog} onClose={() => setCatDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{catDialog?.mode === 'create' ? '新增分類' : '重新命名分類'}</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" sx={{ mt: 1 }} placeholder="例：豬肉料理"
            value={catDialog?.name || ''} onChange={(e) => setCatDialog((d) => ({ ...d, name: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') submitCatDialog(); }} />
          {catDialog?.mode === 'rename' && (
            <FormControlLabel sx={{ mt: 1 }} control={
              <Checkbox checked={categories.find((c) => c.id === catDialog.id)?.isActive === false}
                onChange={() => { const c = categories.find((x) => x.id === catDialog.id); if (c) toggleCatActive(c); }} />
            } label={<Typography sx={{ fontSize: 14 }}>停用此分類（整類暫時下架）</Typography>} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatDialog(null)}>取消</Button>
          <Button variant="contained" onClick={submitCatDialog}>儲存</Button>
        </DialogActions>
      </Dialog>

      {/* 分類刪除確認 */}
      <Dialog open={!!catDeleteTarget} onClose={() => setCatDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>刪除分類</DialogTitle>
        <DialogContent>
          <Typography>
            確定刪除分類 <b>{catDeleteTarget?.name}</b>？分類下還有品項時將無法刪除（請先搬移或刪除品項）。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatDeleteTarget(null)}>取消</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteCat}>刪除</Button>
        </DialogActions>
      </Dialog>

      {/* 品項刪除確認 */}
      <Dialog open={!!itemDeleteTarget} onClose={() => setItemDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>刪除品項</DialogTitle>
        <DialogContent>
          <Typography>確定刪除 <b>{itemDeleteTarget?.name}</b>？已成立訂單的品項快照不受影響。</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setItemDeleteTarget(null)}>取消</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteItem}>刪除</Button>
        </DialogActions>
      </Dialog>

      {/* ═══ 匯入菜單 dialog ═══ */}
      <Dialog open={importOpen} onClose={() => setImportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          匯入菜單
          <IconButton onClick={() => setImportOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: 12.5, color: '#666', mb: 1 }}>
                ① 貼上菜單文字（一行一道：<code>/分類/</code>、<code>★</code>招牌、<code>品名/280</code>、
                <code>品名/時價</code>、<code>品名/(小)200(大)280</code>、下一行括號＝配料說明）
              </Typography>
              <TextField multiline minRows={14} maxRows={20} fullWidth value={importText}
                onChange={(e) => { setImportText(e.target.value); setImportPreview(null); }}
                placeholder={'/豬肉料理/\n干絲豬肉絲/280\n★獅子頭/350\n五更腸旺/280\n(大腸、鴨血)\n/海鮮料理/\n午仔魚(清蒸、乾煎)/時價'}
                sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 13.5 } }} />
              <Button fullWidth variant="outlined" onClick={runParse} disabled={importing || !importText.trim()}
                sx={{ mt: 1.5, borderColor: BLUE, color: BLUE, fontWeight: 700, textTransform: 'none' }}>
                ② 解析 →
              </Button>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12.5, color: '#666', mb: 1 }}>③ 解析結果（匯入後可逐項微調辣度／需預訂／加料群組）</Typography>
              <Paper variant="outlined" sx={{ height: 380, overflowY: 'auto', p: 1.5, bgcolor: '#fafbfc', borderRadius: 2 }}>
                {!importPreview ? (
                  <Typography sx={{ color: '#bbb', textAlign: 'center', mt: 8, fontSize: 14 }}>點「解析」預覽結果</Typography>
                ) : (
                  <>
                    {(importPreview.categories || []).map((c) => (
                      <Box key={c.name} sx={{ mb: 1.5 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#546e7a' }}>{c.name}</Typography>
                        {(c.items || []).map((it, i) => (
                          <Stack key={i} direction="row" justifyContent="space-between" sx={{ py: 0.4, borderBottom: '1px solid #f1f5f9' }}>
                            <Typography sx={{ fontSize: 14 }}>
                              {it.sig ? <Box component="span" sx={{ color: '#f59e0b' }}>★ </Box> : ''}
                              {it.name}
                              {it.kind === 'market_price' && <Badge bg="#ffedd5" fg="#c2410c">時價</Badge>}
                              {it.note && <Box component="span" sx={{ fontSize: 12, color: '#999' }}>（{it.note}）</Box>}
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: '#666', whiteSpace: 'nowrap' }}>
                              {it.kind === 'market_price' ? '時價' : (it.priceOptions || []).map((p) => (p.label ? `${p.label} $${p.price}` : `$${p.price}`)).join('／')}
                            </Typography>
                          </Stack>
                        ))}
                      </Box>
                    ))}
                    {(importPreview.footnotes || []).length > 0 && (
                      <Typography sx={{ fontSize: 11, color: '#999', mt: 1 }}>
                        {importPreview.footnotes.map((f) => `※ ${f}`).join('　')}
                      </Typography>
                    )}
                  </>
                )}
              </Paper>
              <Button fullWidth variant="contained" onClick={runCommit}
                disabled={importing || !importPreview || !importPreview.total}
                sx={{ mt: 1.5, bgcolor: BLUE, '&:hover': { bgcolor: '#0e447a' }, fontWeight: 700, textTransform: 'none' }}>
                ④ 確認匯入{importPreview?.total ? `（${importPreview.total} 道、${importPreview.categories.length} 類）` : ''}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default MenuSettingsPage;
